import type { User } from "../context/AuthContext";
import { pathToScreenKey } from "../constants/screenCatalog";

const FICHA_DIARIA_KEY = "controle_producao_ficha_diaria";
const ITENS_PRODUCAO_KEY = "controle_producao_itens";
const SETORES_PRODUCAO_KEY = "controle_producao_setores";
const FUNCIONAMENTO_KEY = "controle_producao_funcionamento";
export const FICHA_DIARIA_PATH = "/controle-producao/ficha-diaria";
export const ITENS_PRODUCAO_PATH = "/controle-producao/itens";
export const SETORES_PRODUCAO_PATH = "/controle-producao/setores";
export const FUNCIONAMENTO_PATH = "/controle-producao/funcionamento";
const MEU_PERFIL_KEY = "meu_perfil";
const ATUALIZAR_APK_KEY = "atualizar_apk";
const MENSAGENS_MOBILE_KEY = "mensagens_mobile";
const FERRAMENTAS_DESENVOLVEDOR_KEY = "ferramentas_desenvolvedor";

function normalizeRoleNome(nome: string | undefined | null): string {
  return (nome || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

/** Alinhado ao backend (`isAdministradorRoleNome`). */
export function isAdministradorRoleNome(
  nome: string | undefined | null
): boolean {
  return (nome || "").trim().toLowerCase().includes("administrador");
}

export function isLideresRoleNome(nome: string | undefined | null): boolean {
  return normalizeRoleNome(nome) === "lideres";
}

export function isDesenvolvedorRoleNome(nome: string | undefined | null): boolean {
  return normalizeRoleNome(nome) === "desenvolvedor";
}

export function hasFullAccessRoleNome(nome: string | undefined | null): boolean {
  return isAdministradorRoleNome(nome) || isDesenvolvedorRoleNome(nome);
}

export function hasFullAccessUser(user: {
  roleId?: number;
  role?: { nome?: string | null } | null;
} | null): boolean {
  if (!user) return false;
  if (user.roleId === 1 || user.roleId === 10) return true;
  return hasFullAccessRoleNome(user.role?.nome);
}

/** Editar %/quantidades e confirmar o Painel Bordeiro. */
export function canAlterarQuadroPainelBordeiro(user: {
  roleId?: number;
  role?: { nome?: string | null } | null;
} | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user)) return true;
  if (user.roleId === 8) return true;
  return isLideresRoleNome(user.role?.nome);
}

/** Priorizar montagem: flag do perfil, ou admin/dev. */
export function canPriorizarMontagemUser(user: User | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user)) return true;
  return Boolean(user.role?.podePriorizarMontagem);
}

/** Base/sobra na ficha de produção (dia): flag do perfil em Perfis e permissões. */
export function podeVerMaisInfoFichaProducaoUser(user: User | null): boolean {
  return Boolean(user?.role?.podeVerMaisInfoFichaProducao);
}

/** Pode alternar loja e ver dados de todas (prioridade sobre lojaId do usuário). */
export function canVerTodasLojasUser(user: {
  roleId?: number;
  role?: { nome?: string | null; podeVerTodasLojas?: boolean } | null;
} | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user)) return true;
  return Boolean(user.role?.podeVerTodasLojas);
}

export function canSelecionarLojaFichaDiaria(user: User | null): boolean {
  if (!user) return false;
  if (canVerTodasLojasUser(user) || user.roleId === 8) return true;
  return isLideresRoleNome(user.role?.nome);
}

export function canSelecionarLojaOperacional(user: User | null): boolean {
  return canSelecionarLojaFichaDiaria(user);
}

export function canAccessControleProducaoItens(user: User | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user)) return true;
  return Boolean(user.allowedScreenKeys?.includes(ITENS_PRODUCAO_KEY));
}

export function canAccessControleProducaoSetores(user: User | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user)) return true;
  return Boolean(user.allowedScreenKeys?.includes(SETORES_PRODUCAO_KEY));
}

export function canAccessControleProducaoFuncionamento(
  user: User | null
): boolean {
  return hasFullAccessUser(user);
}

export function isBordeiroRoleNome(nome: string | undefined | null): boolean {
  return normalizeRoleNome(nome) === "bordeiro";
}

export function isSupervisorRoleNome(nome: string | undefined | null): boolean {
  const n = normalizeRoleNome(nome);
  return n === "supervisor" || n === "supervisores";
}

/** Ficha completa ao vivo: admin, líder, supervisor ou desenvolvedor. */
export function podeVerFichaCompletaUser(user: User | null): boolean {
  if (!user) return false;
  if (hasFullAccessUser(user) || user.roleId === 8) return true;
  const n = normalizeRoleNome(user.role?.nome);
  return (
    isLideresRoleNome(user.role?.nome) ||
    isSupervisorRoleNome(user.role?.nome) ||
    n === "lider"
  );
}

export function isForneiroRoleNome(nome: string | undefined | null): boolean {
  return normalizeRoleNome(nome) === "forneiro";
}

/** Despachar item do forno: forneiro, bordeiro, administrador ou líderes. */
export function canFinalizarForno(user: User | null): boolean {
  if (!user) return false;
  if (
    hasFullAccessUser(user) ||
    user.roleId === 6 ||
    user.roleId === 7 ||
    user.roleId === 8
  ) {
    return true;
  }
  return (
    isBordeiroRoleNome(user.role?.nome) ||
    isForneiroRoleNome(user.role?.nome) ||
    isLideresRoleNome(user.role?.nome)
  );
}

/** Finalizar bordas: bordeiro, administrador, líderes, supervisor ou desenvolvedor. */
export function canFinalizarFornoOuBordas(user: User | null): boolean {
  if (!user) return false;
  if (
    user.roleId === 1 ||
    user.roleId === 6 ||
    user.roleId === 8 ||
    user.roleId === 10
  ) {
    return true;
  }
  return (
    isAdministradorRoleNome(user.role?.nome) ||
    isBordeiroRoleNome(user.role?.nome) ||
    isLideresRoleNome(user.role?.nome) ||
    isSupervisorRoleNome(user.role?.nome) ||
    isDesenvolvedorRoleNome(user.role?.nome)
  );
}

/** Flags no usuário (`isConferenteBase` / `isProdutor`): só ficha diária. */
export function hasConferenteBaseFichaAccess(user: User | null): boolean {
  return Boolean(user?.isConferenteBase);
}

export function hasProdutorFichaAccess(user: User | null): boolean {
  return Boolean(user?.isProdutor);
}

function hasUserFlagFichaDiaria(user: User | null): boolean {
  return (
    hasConferenteBaseFichaAccess(user) ||
    hasProdutorFichaAccess(user) ||
    Boolean(user?.isSupervisorProducao)
  );
}

/** Perfis de gestão entram no sistema pela dashboard. */
function prefersDashboardHome(user: User | null): boolean {
  if (!user?.role?.nome) return false;
  return (
    isAdministradorRoleNome(user.role.nome) ||
    isLideresRoleNome(user.role.nome) ||
    isDesenvolvedorRoleNome(user.role.nome)
  );
}

/**
 * Sidebar e botão de recolher: qualquer usuário autenticado.
 * As opções exibidas continuam filtradas por `canAccessPath` (perfil + flags do usuário).
 */
export function canShowMaisSidebar(user: User | null): boolean {
  return Boolean(user);
}

export function canShowDesenvolvedorMenu(user: User | null): boolean {
  return isDesenvolvedorRoleNome(user?.role?.nome);
}

function legacyCanAccessPath(user: User | null, pathname: string): boolean {
  if (!user) return false;
  if (
    pathname === "/atualizar-apk" &&
    !hasFullAccessRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    pathname === "/mensagens-mobile" &&
    !hasFullAccessRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    pathname === "/ferramentas-desenvolvedor" &&
    !isDesenvolvedorRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    (pathname === "/monitoramento" ||
      pathname === "/backups" ||
      pathname === "/crons") &&
    !isDesenvolvedorRoleNome(user.role?.nome)
  ) {
    return false;
  }
  const roleName = user.role?.nome?.toLowerCase() || "";
  const isPizzaiolo = roleName === "pizzaiolo";
  const isEntregador = roleName === "entregadores" || roleName === "entregador";
  const isRoterista = roleName === "roterista";
  const isForneiro = roleName === "forneiro";
  const isBordeiro = roleName === "bordeiro";

  const allowedRoutesForPizzaiolo = [
    "/pizzaiolo",
    "/pizzaiolo-integracao",
    "/meu-perfil",
  ];
  const allowedRoutesForEntregador = [
    "/entregadores",
    "/meu-perfil",
    "/ranking/entregas",
  ];
  const allowedRoutesForRoterista = [
    "/roterista",
    "/roterista-automatico",
    "/meu-perfil",
  ];
  const allowedRoutesForForneiro = ["/forneiro", "/meu-perfil"];
  const allowedRoutesForBordeiro = [
    "/bordeiro",
    "/painel-bordeiro",
    "/ranking/painel-bordeiro",
    "/meu-perfil",
  ];

  if (pathname === FICHA_DIARIA_PATH && hasUserFlagFichaDiaria(user)) {
    return true;
  }
  if (pathname === ITENS_PRODUCAO_PATH) {
    return canAccessControleProducaoItens(user);
  }
  if (pathname === SETORES_PRODUCAO_PATH) {
    return canAccessControleProducaoSetores(user);
  }
  if (pathname === FUNCIONAMENTO_PATH) {
    return canAccessControleProducaoFuncionamento(user);
  }

  if (isPizzaiolo) return allowedRoutesForPizzaiolo.includes(pathname);
  if (isEntregador) return allowedRoutesForEntregador.includes(pathname);
  if (isRoterista) return allowedRoutesForRoterista.includes(pathname);
  if (isForneiro) return allowedRoutesForForneiro.includes(pathname);
  if (isBordeiro) return allowedRoutesForBordeiro.includes(pathname);
  return true;
}

export function canAccessPath(user: User | null, pathname: string): boolean {
  if (!user) return false;
  const key = pathToScreenKey(pathname);
  if (key === ATUALIZAR_APK_KEY && !hasFullAccessRoleNome(user.role?.nome)) {
    return false;
  }
  if (
    key === MENSAGENS_MOBILE_KEY &&
    !hasFullAccessRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    key === FERRAMENTAS_DESENVOLVEDOR_KEY &&
    !isDesenvolvedorRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    (key === "monitoramento_servidor" ||
      key === "backups_servidor" ||
      key === "crons_servidor") &&
    !isDesenvolvedorRoleNome(user.role?.nome)
  ) {
    return false;
  }
  if (
    hasFullAccessRoleNome(user.role?.nome) &&
    (key === ATUALIZAR_APK_KEY || key === MENSAGENS_MOBILE_KEY)
  ) {
    return true;
  }
  if (key === MEU_PERFIL_KEY) return true;
  if (key === ITENS_PRODUCAO_KEY) {
    return canAccessControleProducaoItens(user);
  }
  if (key === SETORES_PRODUCAO_KEY) {
    return canAccessControleProducaoSetores(user);
  }
  if (key === FUNCIONAMENTO_KEY) {
    return canAccessControleProducaoFuncionamento(user);
  }

  const keys = user.allowedScreenKeys;
  if (keys === undefined) {
    return legacyCanAccessPath(user, pathname);
  }
  if (!key) return false;
  if (keys.includes(key)) return true;
  if (key === "ranking_ficha_setores" && keys.includes("ranking_ficha_diaria")) {
    return true;
  }
  if (key === FICHA_DIARIA_KEY && hasUserFlagFichaDiaria(user)) {
    return true;
  }
  if (
    key === "controle_producao_ficha_transferencia" &&
    user.isSupervisorProducao
  ) {
    return true;
  }
  if (
    key === "controle_producao_relatorio_equipe" &&
    (hasFullAccessUser(user) ||
      keys.includes("controle_producao_relatorio_tempos"))
  ) {
    return true;
  }
  return false;
}

const HOME_PRIORITY: string[] = [
  "/pizzaiolo-integracao",
  "/pizzaiolo",
  "/entregadores",
  "/roterista-automatico",
  "/roterista",
  "/forneiro",
  "/bordeiro",
  "/dashboard",
  "/painel",
  "/relatorios",
];

/** Itens do menu horizontal principal (fora de dropdowns). */
export const PRIMARY_NAV_PATHS = [
  "/dashboard",
  "/painel",
  "/lojas",
  "/usuarios",
  "/estatisticas",
] as const;

export function hasAccessToAnyPath(
  user: User | null,
  paths: readonly string[]
): boolean {
  return paths.some((p) => canAccessPath(user, p));
}

export function getDefaultHomePath(user: User | null): string {
  if (!user) return "/login";

  if (prefersDashboardHome(user)) {
    const keys = user.allowedScreenKeys;
    if (keys === undefined || canAccessPath(user, "/dashboard")) {
      return "/dashboard";
    }
    if (canAccessPath(user, "/painel")) {
      return "/painel";
    }
  }

  const keys = user.allowedScreenKeys;
  if (keys === undefined) {
    const roleName = user.role?.nome?.toLowerCase() || "";
    if (roleName === "pizzaiolo") return "/pizzaiolo-integracao";
    if (roleName === "entregadores" || roleName === "entregador")
      return "/entregadores";
    if (roleName === "roterista") return "/roterista-automatico";
    if (roleName === "forneiro") return "/forneiro";
    if (roleName === "bordeiro") return "/bordeiro";
    return "/dashboard";
  }
  for (const path of HOME_PRIORITY) {
    if (canAccessPath(user, path)) return path;
  }
  for (const path of ["/lojas", "/usuarios", "/meu-perfil"]) {
    if (canAccessPath(user, path)) return path;
  }
  return "/meu-perfil";
}
